import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase';
import { Card, Button, TextInput, Alert } from 'flowbite-react';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { useDarkMode } from '../contexts/DarkModeContext';
import { shouldUseRedirect } from '../utils/deviceContext';
import { signInWithApple } from '../services/authService';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    // Check for redirect result when component mounts
    getRedirectResult(auth)
      .then((result) => {
        if (result) {
          navigate('/adhan-clock');
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/adhan-clock');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      if (shouldUseRedirect()) {
        await signInWithRedirect(auth, provider);
        // The redirect will happen here, and the result will be handled in useEffect
      } else {
        await signInWithPopup(auth, provider);
        navigate('/adhan-clock');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAppleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithApple();
      navigate('/adhan-clock');
    } catch (error) {
      console.error('Apple Sign-In error:', error);
      // Make error message more user-friendly
      if (error.code === 'auth/popup-blocked') {
        setError('Please enable popups for this website to sign in with Apple');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Sign-in was cancelled');
      } else if (error.message && error.message.includes('AuthenticationServices')) {
        // Handle native Apple authentication errors
        if (error.message.includes('error 1000')) {
          setError('Apple Sign-In was cancelled');
        } else if (error.message.includes('error 1001')) {
          setError('Apple Sign-In failed: Invalid parameters');
        } else if (error.message.includes('error -3')) {
          setError('Unable to connect to Apple services. Please check your internet connection.');
        } else {
          // Generic message for other AuthenticationServices errors
          setError('Apple Sign-In failed. Please try again or use another sign-in method.');
        }
      } else {
        // For other errors, provide a cleaner message
        setError('Unable to sign in with Apple. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isNative = typeof window !== 'undefined' && window.isUmmahtiNative;

  return (
    <div className={`flex flex-col min-h-screen relative overflow-hidden transition-colors duration-200 ${
      isDarkMode ? 'bg-[#0A0A0A]' : 'bg-gray-50'
    }`}>
      {isNative ? (
        <>
          {/* Background Image with Gradient Overlay - Native App */}
          <div className="absolute inset-0">
            <img
              src="/images/headerbg.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50"
            />
            <div className={`absolute inset-0 ${
              isDarkMode
                ? 'bg-gradient-to-b from-emerald-900/95 via-[#111111]/98 to-[#111111]'
                : 'bg-gradient-to-b from-emerald-600/95 via-white/98 to-white'
            }`} />
          </div>

          {/* Gradient Orbs - Native App */}
          <div className={`absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-500/[0.07]'
              : 'bg-emerald-200/40'
          }`} />
          <div className={`absolute bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 ${
            isDarkMode
              ? 'bg-emerald-500/[0.07]'
              : 'bg-emerald-100/60'
          }`} />
        </>
      ) : (
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A] to-[#041E1B]' 
            : 'bg-gradient-to-b from-gray-50 via-white to-emerald-50/30'
        }`} />
      )}

      <div className="relative flex-1 flex items-center justify-center px-4" 
           style={{ paddingTop: isNative ? 'calc(env(safe-area-inset-top) + 2rem)' : '2rem' }}>
        <div className={`w-full max-w-md ${
          isNative ? 'bg-transparent' : `rounded-[24px] overflow-hidden ${
            isDarkMode 
              ? 'bg-[#111111] shadow-lg shadow-black/5 border border-white/10' 
              : 'bg-white shadow-sm border border-gray-200'
          }`
        }`}>
          <div className="p-8">
            {/* Logo - Only show in native app */}
            {isNative && (
              <div className="flex justify-center mb-12">
                <div className="w-24 h-24">
                  <img
                    src="/images/ummahti_white_transparent.png"
                    alt="Ummahti"
                    className={`w-full transition-opacity duration-300 ${isDarkMode ? 'opacity-90' : 'opacity-80'}`}
                  />
                </div>
              </div>
            )}

            <h2 className={`text-2xl font-light mb-8 text-center ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Welcome back
            </h2>
            
            {error && (
              <Alert 
                color="failure" 
                className="mb-6"
                onDismiss={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <TextInput
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`${isDarkMode ? '[&>input]:bg-[#1A1A1A] [&>input]:border-gray-800' : ''} ${
                    isNative ? '[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [&>input]:backdrop-blur-md' : ''
                  }`}
                />
              </div>
              <div>
                <TextInput
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`${isDarkMode ? '[&>input]:bg-[#1A1A1A] [&>input]:border-gray-800' : ''} ${
                    isNative ? '[&>input]:rounded-2xl [&>input]:px-6 [&>input]:py-4 [&>input]:backdrop-blur-md' : ''
                  }`}
                />
                <div className="mt-1 text-right">
                  <Link 
                    to="/forgot-password"
                    className={`text-xs ${
                      isDarkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-500'
                    }`}
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
              <Button 
                type="submit" 
                className={`w-full ${
                  isNative ? 'py-4 px-6 rounded-2xl backdrop-blur-md' : ''
                } ${
                  isDarkMode 
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-black font-medium' 
                    : 'bg-gray-900 hover:bg-gray-800 text-white'
                }`}
              >
                Sign in
              </Button>
            </form>

            <div className="relative my-6">
              <div className={`absolute inset-0 flex items-center`}>
                <div className={`w-full border-t ${
                  isDarkMode ? 'border-gray-800' : 'border-gray-200'
                }`}></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className={`px-2 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                } ${isNative ? 'backdrop-blur-md' : ''}`}>
                  Or continue with
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <Button 
                color="light"
                className={`w-full ${
                  isNative ? 'py-4 px-6 rounded-2xl backdrop-blur-md' : ''
                } ${
                  isDarkMode 
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/[0.05]' 
                    : 'bg-black/10 hover:bg-black/20 text-gray-900 border border-black/[0.05]'
                }`}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                <FcGoogle className="mr-2 h-5 w-5" />
                {isLoading ? 'Signing in...' : 'Sign in with Google'}
              </Button>
              
              <Button 
                color="dark"
                className={`w-full ${
                  isNative ? 'py-4 px-6 rounded-2xl backdrop-blur-md' : ''
                } ${
                  isDarkMode 
                    ? 'bg-black hover:bg-gray-900 text-white border border-white/[0.05]' 
                    : 'bg-black hover:bg-gray-900 text-white border border-black/[0.05]'
                }`}
                onClick={handleAppleSignIn}
                disabled={isLoading}
              >
                <FaApple className="mr-2 h-5 w-5" />
                {isLoading ? 'Signing in...' : 'Sign in with Apple'}
              </Button>
            </div>

            <p className={`mt-6 text-center text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className={isDarkMode ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-500'}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer Text - Only show in native app */}
      {isNative && (
        <div className="relative pb-8 px-8 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      )}
    </div>
  );
};

export default Login;